import { createContext, useContext } from "react";

/**
 * Iter-158.4.0 — ApiBase context.
 *
 * The H&M pages currently hardcode `/projects/…` in every `api.get`
 * call. To make those same pages reusable inside the beta H&M
 * workspace (which will target `/beta/hm-app/projects/…`) we introduce
 * a single indirection: each page reads its API base from context via
 * `useApiBase()` instead of hardcoding `/projects/`.
 *
 * The default value is `/projects` so ANY page that hasn't been
 * refactored yet — or is used outside a provider — continues to
 * behave exactly like today. This is the critical safety property
 * that lets us roll the refactor out in tranches without visible
 * legacy regressions.
 *
 * Usage inside a page:
 *
 *     const apiBase = useApiBase();
 *     api.get(`${apiBase}/${projectId}/…`)
 *
 * The value is expected to be a bare path segment starting with a
 * slash (e.g. `/projects` or `/beta/hm-app/projects`) — the axios
 * instance's baseURL already includes `/api`.
 */
export const ApiBaseContext = createContext("/projects");

export function ApiBaseProvider({ value, children }) {
    return (
        <ApiBaseContext.Provider value={value ?? "/projects"}>
            {children}
        </ApiBaseContext.Provider>
    );
}

export function useApiBase() {
    return useContext(ApiBaseContext);
}

/**
 * Iter-158.5.2.a — Namespace Seal.
 *
 * Route base for React Router paths. Legacy → `/projects`;
 * Beta H&M workspace → `/beta/hm-app/projects`. The two happen to
 * mirror the API base 1:1 because the beta transplant deliberately
 * chose the same URL segment structure, so this hook piggy-backs on
 * the same context provider — no separate wiring needed. Shared
 * components consume this via:
 *
 *     const routeBase = useRouteBase();
 *     navigate(`${routeBase}/${projectId}/cast`);
 *     <Link to={`${routeBase}/${projectId}/scenes/${sceneId}`} />
 *
 * so a click from inside `/beta/hm-app/projects/…` never leaks back
 * out into the legacy `/projects/…` namespace.
 */
export function useRouteBase() {
    return useContext(ApiBaseContext);
}
